<template>
  <div class="ytd-row">
    <div v-if="hasError" class="ytd-row-state ytd-row-error">
      <div class="ytd-error-msg">Unable to load Year to Date data.</div>
      <DxButton text="Retry" @click="load" />
    </div>

    <template v-else>
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="ytd-tile"
      >
        <div v-if="isLoading" class="ytd-tile-loading">
          <DxLoadIndicator :height="18" :width="18" />
        </div>
        <template v-else>
          <div class="ytd-lbl">{{ tile.label }}</div>
          <div class="ytd-val-row">
            <div class="ytd-val">
              {{ tile.value }}
              <span v-if="tile.currencyCode" class="ytd-val-currency">{{ tile.currencyCode }}</span>
            </div>
            <div class="ytd-trend" :class="tile.trendClass">
              <svg v-if="tile.trendDirection === 'up'" width="11" height="11" viewBox="0 0 10 10">
                <polygon points="5,1 9,9 1,9" fill="currentColor" />
              </svg>
              <svg v-else-if="tile.trendDirection === 'down'" width="11" height="11" viewBox="0 0 10 10">
                <polygon points="1,1 9,1 5,9" fill="currentColor" />
              </svg>
              <span>{{ tile.trendText }}</span>
            </div>
          </div>
        </template>
      </div>

      <div class="ytd-tile ytd-tile-outstanding">
        <div v-if="balancesSummary === null" class="ytd-tile-loading">
          <DxLoadIndicator :height="18" :width="18" />
        </div>
        <template v-else>
          <div class="ytd-lbl">Outstanding</div>
          <div class="ytd-val-row">
            <div class="ytd-val ytd-val-neg">
              {{ outstandingValue }}
              <span v-if="fxCurrencyCode" class="ytd-val-currency">{{ fxCurrencyCode }}</span>
            </div>
            <div class="ytd-sub">
              Overdue {{ overdueValue }}
              <span v-if="fxCurrencyCode" class="ytd-val-currency">{{ fxCurrencyCode }}</span>
            </div>
          </div>
          <div v-if="outstandingTotal > 0" class="ytd-outstanding-bar">
            <div class="ytd-outstanding-bar-overdue" :style="{ width: overdueRatio + '%' }" />
            <div class="ytd-outstanding-bar-current" :style="{ width: (100 - overdueRatio) + '%' }" />
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { DxButton } from "devextreme-vue/button";
import { DxLoadIndicator } from "devextreme-vue/load-indicator";
import { CustomerPerformanceService } from "../../_services/api/customers/customer-performance.service";
import { formatCurrencyKM } from "../../_helpers/formats/format.js";

export default {
  name: "GcDashboardYtdCard",
  components: { DxButton, DxLoadIndicator },
  props: {
    customerCode: {
      type: String,
      required: true,
    },
    balancesSummary: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      ytd: null,
      monthly: null,
    };
  },
  computed: {
    tiles() {
      const ytd = this.ytd || {};

      return [
        {
          key: "totalSales",
          label: "Total Sales YTD",
          value: formatCurrencyKM(ytd.FxThisYearGrossValue, false),
          currencyCode: ytd.FxCurrencyCode,
          ...this.buildTrend(ytd.FxThisYearGrossValue, ytd.FxLastYearGrossValue, false),
        },
        {
          key: "grossProfit",
          label: "Gross Profit YTD",
          value: formatCurrencyKM(ytd.FxThisYearMarginValue, false),
          currencyCode: ytd.FxCurrencyCode,
          ...this.buildTrend(ytd.FxThisYearMarginValue, ytd.FxLastYearMarginValue, false),
        },
        {
          key: "gpPct",
          label: "Gross Profit %",
          value: this.formatPercent(ytd.ThisYearMarginPcnt),
          currencyCode: null,
          ...this.buildTrend(ytd.ThisYearMarginPcnt, ytd.LastYearMarginPcnt, true),
        },
      ];
    },
    outstandingBuckets() {
      return (this.balancesSummary && this.balancesSummary.AgingByTerms) || [];
    },
    outstandingTotal() {
      return this.outstandingBuckets.reduce((sum, b) => sum + (Number(b.FxAmount) || 0), 0);
    },
    overdueTotal() {
      return this.outstandingBuckets.reduce((sum, b) => sum + (Number(b.OverdueFxAmount) || 0), 0);
    },
    outstandingValue() {
      return formatCurrencyKM(this.outstandingTotal, false, true);
    },
    overdueValue() {
      return formatCurrencyKM(this.overdueTotal, false, true);
    },
    overdueRatio() {
      if (!this.outstandingTotal) return 0;
      return Math.min(100, Math.max(0, (this.overdueTotal / this.outstandingTotal) * 100));
    },
    fxCurrencyCode() {
      return this.balancesSummary && this.balancesSummary.FxCurrencyCode;
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const [ytdRes, monthlyRes] = await Promise.all([
          CustomerPerformanceService.getYtd(this.customerCode),
          CustomerPerformanceService.getMonthly(this.customerCode),
        ]);
        this.ytd = ytdRes.data || null;
        this.monthly = monthlyRes.data || null;
      } catch (err) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async refresh() {
      await this.load();
    },
    formatPercent(value) {
      if (value === null || value === undefined) return "—";
      return `${Number(value).toFixed(2)}%`;
    },
    buildTrend(thisYear, lastYear, isPercentageMetric) {
      if (
        thisYear === null ||
        thisYear === undefined ||
        lastYear === null ||
        lastYear === undefined ||
        Number(lastYear) === 0
      ) {
        return {
          trendText: "N/A",
          trendClass: "t-neutral",
          trendDirection: "none",
        };
      }
      const diff = isPercentageMetric
        ? Number(thisYear) - Number(lastYear)
        : ((Number(thisYear) - Number(lastYear)) / Number(lastYear)) * 100;
      const rounded = Math.abs(diff).toFixed(2);
      const unit = isPercentageMetric ? "pp" : "%";
      return {
        trendText: `${rounded}${unit}`,
        trendClass: diff >= 0 ? "t-up" : "t-down",
        trendDirection: diff >= 0 ? "up" : "down",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.ytd-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.ytd-row-state {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 0;
  background: #fff;
  border: 1px solid var(--base-border-color, #e0e0e0);
  border-radius: 6px;

  .ytd-error-msg {
    color: #e8384f;
    font-size: 12px;
  }
}

.ytd-tile {
  background: #fff;
  border: 1px solid var(--base-border-color, #e0e0e0);
  border-radius: 6px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 95px;
}

.ytd-tile-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ytd-lbl {
  font-size: 12px;
  color: #667085;
  letter-spacing: 0.4px;
}

.ytd-val-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.ytd-val {
  font-size: 24px;
  font-weight: bold;
  color: #323842;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.ytd-val-neg {
  color: #d92d20;
}

.ytd-val-currency {
  font-size: 11px;
  font-weight: 500;
  color: #98a2b3;
  margin-left: 4px;
}

.ytd-sub {
  font-size: 11px;
  font-weight: 600;
  color: #d92d20;
}

.ytd-outstanding-bar {
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: #eef0f3;
}

.ytd-outstanding-bar-overdue {
  background: #d92d20;
}

.ytd-outstanding-bar-current {
  background: #eec300;
}

.ytd-trend {
  display: flex;
  gap: 3px;
  font-size: 11.5px;
  font-weight: bold;

  &.t-up {
    color: #027a48;
  }

  &.t-down {
    color: #d92d20;
  }

  &.t-neutral {
    color: #667085;
  }
}
</style>
